<template>
  <div class="wx-dash">
    <!-- Greeting + quick links -->
    <section class="wx-dhero">
      <div class="wx-dhero-txt">
        <p>{{ todayLabel }}</p>
        <h2>{{ greeting }} 👋</h2>
      </div>
      <div class="wx-dhero-actions">
        <button type="button" class="btn" @click="showModal()"><WxIcon name="grid" :size="16" />Items</button>
        <button v-if="superUser || canViewDashboard" type="button" class="btn btn-secondary" @click="goToEditDashboard">
          <WxIcon name="settings" :size="16" />Edit widgets
        </button>
      </div>
    </section>

    <nav class="wx-quick">
      <RouterLink v-for="item in dashboardItems" :key="item.id" :to="item.link" class="wx-q">
        <span class="wx-qi"><WxIcon :name="itemIcon(item.title)" :size="18" /></span>
        <span>{{ item.title }}</span>
      </RouterLink>
      <RouterLink to="/notifications" class="wx-q">
        <span class="wx-qi"><WxIcon name="bell" :size="18" /></span>
        <span>Notifications</span>
      </RouterLink>
    </nav>

    <div v-if="superUser || canViewDashboard" class="wx-bento">
      <!-- KPI tiles -->
      <template v-for="(widget, index) in widgets" :key="widget.code">
        <div v-if="widget.enabled" class="wx-kpi" :class="'k' + (index + 1)" :data-widget-code="widget.code">
          <div class="wx-ring" :style="{ '--p': (widget.percentage || 0) + '%' }"><span>{{ Math.round(widget.percentage || 0) }}%</span></div>
          <div class="l">{{ widget.title }}</div>
          <div class="v">{{ Math.round(widget.percentage || 0) }}<small>%</small></div>
        </div>
      </template>
      <RouterLink v-if="hostingStatusEnabled" to="/hosting" class="wx-kpi k4" data-widget-code="hosting_status">
        <div class="l">Hosting status</div>
        <div class="v">{{ hostingStatus.expires_soon ?? 0 }}<small> expiring soon</small></div>
        <div class="wx-kpi-row">
          <span><b>{{ hostingStatus.expired ?? 0 }}</b> expired</span>
          <span><b>{{ hostingStatus.active ?? 0 }}</b> active</span>
        </div>
      </RouterLink>

      <!-- Overview chart -->
      <div v-if="overviewEnabled" class="card wx-s8">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6>Overview</h6>
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item"><a class="nav-link" :class="{ active: activeOverviewTab === 'project' }" href="#" @click.prevent="setActiveOverviewTab('project')">Projects</a></li>
            <li class="nav-item"><a class="nav-link" :class="{ active: activeOverviewTab === 'task' }" href="#" @click.prevent="setActiveOverviewTab('task')">Tasks</a></li>
            <li class="nav-item"><a class="nav-link" :class="{ active: activeOverviewTab === 'subtask' }" href="#" @click.prevent="setActiveOverviewTab('subtask')">Subtasks</a></li>
          </ul>
        </div>
        <div class="card-body widget-content" data-widget-code="overview">
          <div id="hiringsources" style="min-height: 300px;"></div>
        </div>
      </div>

      <!-- Side column -->
      <div class="wx-s4 wx-stack">
        <div v-if="upcomingHolidayEnabled && hasHR" class="card" data-widget-code="upcoming_holiday">
          <div class="card-body wx-holiday">
            <span class="wx-hi">🎉</span>
            <div>
              <div class="wx-muted">Επόμενη αργία</div>
              <b>{{ upcomingHoliday.title || '—' }}</b>
              <div class="wx-muted" v-if="upcomingHoliday.days_left === 0">αύριο</div>
              <div class="wx-muted" v-else-if="upcomingHoliday.days_left === 1">μεθαύριο</div>
              <div class="wx-muted" v-else-if="upcomingHoliday.days_left > 1">σε {{ upcomingHoliday.days_left }} ημέρες</div>
            </div>
          </div>
        </div>
        <div v-if="employeesStatusEnabled && hasHR" class="card" data-widget-code="employees_status">
          <div class="card-header"><h6>Employees status</h6></div>
          <div class="card-body wx-team">
            <div style="--c: var(--wx-done)"><b>{{ employeesStatus.available_count ?? 0 }}</b><span>Working</span></div>
            <div style="--c: var(--wx-hold)"><b>{{ employeesStatus.in_vacation_count ?? 0 }}</b><span>On vacation</span></div>
          </div>
        </div>
        <div v-if="daysOffEnabled && hasHR" class="card" data-widget-code="days_off">
          <div class="card-header"><h6>Days off</h6></div>
          <div class="card-body wx-team">
            <div style="--c: var(--wx-accent)"><b>{{ daysOff.total_days_off ?? 0 }}</b><span>Total days off</span></div>
            <div style="--c: var(--wx-done)"><b>{{ daysOff.remaining_days ?? 0 }}</b><span>Total avail. days</span></div>
          </div>
        </div>
        <div v-if="participationEnabled" class="card" data-widget-code="participation">
          <div class="card-header"><h6>Participation</h6></div>
          <div class="card-body wx-team three">
            <div style="--c: var(--wx-accent)"><b>{{ participation.projects_participated ?? 0 }}</b><span>Projects participated</span></div>
            <div style="--c: var(--wx-progress)"><b>{{ participation.completed_tasks ?? 0 }}</b><span>Completed tasks</span></div>
            <div style="--c: var(--wx-done)"><b>{{ participation.completed_subtasks ?? 0 }}</b><span>Completed subtasks</span></div>
          </div>
        </div>
        <!-- Top projects -->
        <div v-if="topWorkedProjectsEnabled" class="card" data-widget-code="top_worked_projects_last_month">
          <div class="card-header"><h6>Top worked projects last month</h6></div>
          <div class="card-body">
            <div v-for="project in topWorkedProjects" :key="project.project_name" class="wx-bar-row">
              <div class="wx-bar-top"><b>{{ project.project_name }}</b><span>{{ roundToTwo(project.hours) }} h</span></div>
              <div class="wx-prog"><i :style="{ width: barWidth(project.hours) }"></i></div>
            </div>
            <div v-if="!topWorkedProjects.length" class="wx-muted">No data!</div>
          </div>
        </div>
      </div>

      <!-- To do -->
      <div v-if="todoEnabled" class="card wx-s8" data-widget-code="todo">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6>To do List</h6>
          <div class="d-flex flex-wrap gap-2">
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoTab === 'projects' }" href="#" @click.prevent="setActiveTodoTab('projects')">Projects</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoTab === 'tasks' }" href="#" @click.prevent="setActiveTodoTab('tasks')">Tasks</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoTab === 'subtasks' }" href="#" @click.prevent="setActiveTodoTab('subtasks')">Subtasks</a></li>
            </ul>
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoStatusTab === 'on_hold' }" href="#" @click.prevent="setActiveTodoStatusTab('on_hold')">On Hold</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoStatusTab === 'on_progress' }" href="#" @click.prevent="setActiveTodoStatusTab('on_progress')">In Progress</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoStatusTab === 'done' }" href="#" @click.prevent="setActiveTodoStatusTab('done')">Done</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeTodoStatusTab === 'ongoing' }" href="#" @click.prevent="setActiveTodoStatusTab('ongoing')">On Going</a></li>
            </ul>
          </div>
        </div>
        <div class="card-body">
          <div v-if="todoItems.length === 0" class="wx-muted wx-empty">No data!</div>
          <a v-for="item in todoItems" :key="item.id" class="wx-li" :href="getLink(item)">
            <span class="wx-dot" :class="getStatusClass(item.statusId)"></span>
            <div class="wx-li-main">
              <b>{{ item.code || item.title || '' }}</b>
              <span class="wx-muted">{{ clientName(item, activeTodoTab) }} · {{ item.manager?.username || '-' }}</span>
            </div>
            <span class="wx-date">{{ formatDate(item.endDate) || '-' }}</span>
            <span :class="getStatusClass(item.statusId)">{{ getStatusText(item.statusId) }}</span>
          </a>
        </div>
      </div>

      <!-- Follow up -->
      <div v-if="followUpEnabled" class="card wx-s6" data-widget-code="followUp">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6>Follow up</h6>
          <div class="d-flex flex-wrap gap-2">
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpTab === 'projects' }" href="#" @click.prevent="setActiveFollowUpTab('projects')">Projects</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpTab === 'tasks' }" href="#" @click.prevent="setActiveFollowUpTab('tasks')">Tasks</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpTab === 'subtasks' }" href="#" @click.prevent="setActiveFollowUpTab('subtasks')">Subtasks</a></li>
            </ul>
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpStatusTab === 'on_hold' }" href="#" @click.prevent="setActiveFollowUpStatusTab('on_hold')">On Hold</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpStatusTab === 'on_progress' }" href="#" @click.prevent="setActiveFollowUpStatusTab('on_progress')">In Progress</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpStatusTab === 'done' }" href="#" @click.prevent="setActiveFollowUpStatusTab('done')">Done</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeFollowUpStatusTab === 'ongoing' }" href="#" @click.prevent="setActiveFollowUpStatusTab('ongoing')">On Going</a></li>
            </ul>
          </div>
        </div>
        <div class="card-body">
          <div v-if="followUpItems.length === 0" class="wx-muted wx-empty">No data!</div>
          <a v-for="item in followUpItems" :key="item.id" class="wx-li" :href="getLink(item)">
            <span class="wx-dot" :class="getStatusClass(item.statusId)"></span>
            <div class="wx-li-main">
              <b>{{ item.code || item.title || '' }}</b>
              <span class="wx-muted">{{ clientName(item, activeFollowUpTab) }} · {{ item.manager?.username || '-' }}</span>
            </div>
            <span class="wx-date">{{ formatDate(item.endDate) || '-' }}</span>
            <span :class="getStatusClass(item.statusId)">{{ getStatusText(item.statusId) }}</span>
          </a>
        </div>
      </div>

      <!-- Deadline coming soon -->
      <div v-if="deadlineEnabled" class="card wx-s6" data-widget-code="deadline_coming_soon">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6>Deadline coming soon</h6>
          <div class="d-flex flex-wrap gap-2">
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item"><a class="nav-link" :class="{ active: activeDeadlineTab === 'projects' }" href="#" @click.prevent="setActiveDeadlineTab('projects')">Projects</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeDeadlineTab === 'tasks' }" href="#" @click.prevent="setActiveDeadlineTab('tasks')">Tasks</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeDeadlineTab === 'subtasks' }" href="#" @click.prevent="setActiveDeadlineTab('subtasks')">Subtasks</a></li>
            </ul>
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item"><a class="nav-link" :class="{ active: activeDeadlineStatusTab === 'on_hold' }" href="#" @click.prevent="setActiveDeadlineStatusTab('on_hold')">On Hold</a></li>
              <li class="nav-item"><a class="nav-link" :class="{ active: activeDeadlineStatusTab === 'on_progress' }" href="#" @click.prevent="setActiveDeadlineStatusTab('on_progress')">In Progress</a></li>
            </ul>
          </div>
        </div>
        <div class="card-body">
          <div v-if="deadlineItems.length === 0" class="wx-muted wx-empty">No data!</div>
          <a v-for="item in deadlineItems" :key="item.id" class="wx-li" :href="getLink(item)">
            <span class="wx-dot" :class="getStatusClass(item.statusId)"></span>
            <div class="wx-li-main">
              <b>{{ item.code || item.title || '' }}</b>
              <span class="wx-muted">{{ clientName(item, activeDeadlineTab) }} · {{ item.manager?.username || '-' }}</span>
            </div>
            <span class="wx-date late">{{ formatDate(item.endDate) || '-' }}</span>
            <span :class="getStatusClass(item.statusId)">{{ getStatusText(item.statusId) }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="dashboard-confirmation-modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Επιλογή Εμφανιζόμενων Ενοτήτων</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <label v-for="item in allDashboardItems" :key="item.id" :for="'item-' + item.id" class="wx-check-row">
            <input type="checkbox" :id="'item-' + item.id" v-model="item.isActive" class="form-check-input" />
            <WxIcon :name="itemIcon(item.title)" :size="18" />
            <span>{{ item.title }}</span>
          </label>
        </div>
        <div class="modal-footer">
          <button class="btn" data-bs-dismiss="modal">Άκυρο</button>
          <button class="btn btn-secondary" @click="saveDashboardPreferences">Αποθήκευση</button>
        </div>
      </div>
    </div>
  </div>
</template>



<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useFetchData } from '@/composables/useFetchData';
import { useRoute, useRouter } from 'vue-router';
import 'vue-toastification/dist/index.css';
import { mapGetters } from 'vuex';
import { fetchUserInfo, fetchDashboardItems, save } from '@/services/userInfo';
import { useToast } from 'vue-toastification';
import router from '@/router';
import ApexCharts from 'apexcharts';
import apiClient from '@/services/api';
import WxIcon from '@/components/WxIcon.vue';

export default {
  name: 'Dashboard',
  components: { WxIcon },
  data() {
    return {
      widgets: [],
      overviewEnabled: false,
      hostingStatusEnabled: false,
      todoEnabled: false,
      followUpEnabled: false,
      deadlineEnabled: false,
      upcomingHolidayEnabled: false,
      daysOffEnabled: false,
      participationEnabled: false,
      employeesStatusEnabled: false,
      topWorkedProjectsEnabled: false,
      overviewChart: null,
      todoItems: [],
      followUpItems: [],
      deadlineItems: [],
      hostingStatus: {},
      upcomingHoliday: {},
      daysOff: {},
      participation: {},
      employeesStatus: {},
      topWorkedProjects: [],
      activeOverviewTab: 'project',
      activeTodoTab: 'projects',
      activeTodoStatusTab: 'on_progress',
      activeFollowUpTab: 'projects',
      activeFollowUpStatusTab: 'on_progress',
      activeDeadlineTab: 'projects',
      activeDeadlineStatusTab: 'on_progress',
    };
  },
  computed: {
    ...mapGetters({
      getUserPermission: 'getUserPermission',
      getSuperUserPermission: 'getSuperUserPermission',
      getUserPackets: 'getUserPackets'
    }),
    userPermissionsProjects() {
      return this.getUserPermission('Projects');
    },
    canViewProjects() {
      return this.userPermissionsProjects?.view === 1;
    },
    userPermissionsUsers() {
      return this.getUserPermission('Users');
    },
    canViewUsers() {
      return this.userPermissionsUsers?.view === 1;
    },
    userPermissionsServices() {
      return this.getUserPermission('Services');
    },
    canViewServices() {
      return this.userPermissionsServices?.view === 1;
    },
    userPermissionsSuppliers() {
      return this.getUserPermission('Suppliers');
    },
    canViewSuppliers() {
      return this.userPermissionsSuppliers?.view === 1;
    },
    userPermissionsClients() {
      return this.getUserPermission('Clients');
    },
    userPermissionsDashboard() {
      return this.getUserPermission('Dashboard');
    },
    canViewClients() {
      return this.userPermissionsClients?.view === 1;
    },
    userPermissionsContacts() {
      return this.getUserPermission('Contacts');
    },
    userPermissionsLists() {
      return this.getUserPermission('Lists');
    },
    userPermissionsGroups() {
      return this.getUserPermission('Service Categories');
    },
    canViewContacts() {
      return this.userPermissionsContacts?.view === 1;
    },
    canViewLists() {
      return this.userPermissionsLists?.view === 1;
    },
    canViewGroups() {
      return this.userPermissionsGroups?.view === 1;
    },
    userPermissionsHours() {
      return this.getUserPermission('Hours');
    },
    canViewHours() {
      return this.userPermissionsHours?.view === 1;
    },
    userPermissionsHolidays() {
      return this.getUserPermission('Holidays');
    },
    canViewHolidays() {
      return this.userPermissionsHolidays?.view === 1;
    },

    userPermissionsReporting() {
      return this.getUserPermission('Reporting');
    },
    canViewReporting() {
      return this.userPermissionsReporting?.view === 1;
    },
    userPermissionsDaysOff() {
      return this.getUserPermission('Days off');
    },
    canViewDaysOff() {
      return this.userPermissionsDaysOff?.view === 1;
    },
    userPermissionsDaysOffReports() {
      return this.getUserPermission('Days off reports');
    },
    canViewDaysOffReports() {
      return this.userPermissionsDaysOffReports?.view === 1;
    },
    greeting() {
      const h = new Date().getHours();
      return h < 12 ? 'Καλημέρα' : h < 18 ? 'Καλό απόγευμα' : 'Καλησπέρα';
    },
    todayLabel() {
      const s = new Date().toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' });
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    canViewDashboard() {
      return this.userPermissionsDashboard?.view === 1;
    },
    superUser() {
      return this.getSuperUserPermission === 1;
    },

    // Packet-based feature flags
    hasCRM() {
      return this.getUserPackets.includes('CRM');
    },
    hasHR() {
      return this.getUserPackets.includes('HR');
    },
    hasProjectManagement() {
      return this.getUserPackets.includes('PROJECT MANAGEMENT');
    },
    hasMarketingHub() {
      return this.getUserPackets.includes('MARKETING HUB');
    },
    // Add more like:
    hasMED() {
      return this.getUserPackets.includes('MED');
    }
  },
  methods: {
    itemIcon(title) {
      const t = (title || '').toLowerCase();
      if (t.includes('project')) return 'briefcase';
      if (t.includes('client') || t.includes('contact') || t.includes('supplier') || t.includes('user')) return 'users';
      if (t.includes('hour')) return 'clock';
      if (t.includes('host')) return 'server';
      if (t.includes('day') || t.includes('holiday') || t.includes('request')) return 'calendar';
      if (t.includes('report')) return 'chart';
      if (t.includes('list')) return 'megaphone';
      if (t.includes('do') || t.includes('follow')) return 'list';
      return 'grid';
    },
    clientName(item, tab) {
      if (tab === 'tasks') return item.project?.client?.nickname || '-';
      if (tab === 'subtasks') return item.task?.project?.client?.nickname || '-';
      return item.client?.nickname || '-';
    },
    barWidth(hours) {
      const max = Math.max(...this.topWorkedProjects.map((p) => Number(p.hours) || 0), 1);
      return Math.max(4, Math.round((Number(hours) || 0) / max * 100)) + '%';
    },
    showModal() {
      $('#dashboard-confirmation-modal').modal('show');
    }, goToEditDashboard() {
      router.push({ path: '/edit-dashboard' });
    },
    formatDate(dateStr) {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    roundToTwo(num) {
      if (num == null) return '-';
      return Number(num).toFixed(2);
    },
    async fetchStatistics() {
      const data = await apiClient.get('/dashboard/get-statistics');
      return data;
    },
    async fetchUserWidgets() {
      const data = await apiClient.get('/fetch-widgets-users');
      return data;
    },
    async fetchTodoItems() {
      const type = this.getActiveTodoType();
      const status = this.getActiveTodoStatusId();
      const data = await apiClient.get(`/dashboard/get-todo-${type}?statusId=${status}`);
      this.todoItems = data?.data?.data?.todo?.[type] || [];
    },
    async fetchFollowUpItems() {
      const type = this.activeFollowUpTab;
      const status = this.getActiveFollowUpStatusId();
      const data = await apiClient.get(`/dashboard/get-followup-${type}?statusId=${status}`);
      this.followUpItems = data?.data?.data?.followUp?.[type] || [];
    },
    async fetchDeadlineItems() {
      const type = this.activeDeadlineTab;
      const status = this.getActiveDeadlineStatusId();
      const data = await apiClient.get(`/dashboard/get-deadline_coming_soon-${type}?statusId=${status}`);
      this.deadlineItems = data?.data?.data?.deadline_coming_soon?.[type] || [];
    },
    getLink(item) {
      const type = this.getActiveTodoType();
      const linkPath = type === 'projects' ? 'projectId' : type === 'tasks' ? 'taskId' : 'subtaskId';
      return `/project?${linkPath}=${item.id}`;
    },
    getStatusClass(statusId) {
      const statusMap = {
        1: 'on_hold-span',
        2: 'on_progress-span',
        3: 'done-span',
        4: 'archived-span',
        5: 'ongoing-span',
      };
      return statusMap[statusId] || '';
    },
    getStatusText(statusId) {
      const statusMap = {
        1: 'On hold',
        2: 'In progress',
        3: 'Done',
        4: 'Archived',
        5: 'On going',
      };
      return statusMap[statusId] || '';
    },
    setActiveOverviewTab(tab) {
      this.activeOverviewTab = tab;
      this.fetchOverview(tab);
    },
    setActiveTodoTab(tab) {
      this.activeTodoTab = tab;
      this.fetchTodoItems();
    },
    setActiveTodoStatusTab(statusTab) {
      this.activeTodoStatusTab = statusTab;
      this.fetchTodoItems();
    },
    setActiveFollowUpTab(tab) {
      this.activeFollowUpTab = tab;
      this.fetchFollowUpItems();
    },
    setActiveFollowUpStatusTab(statusTab) {
      this.activeFollowUpStatusTab = statusTab;
      this.fetchFollowUpItems();
    },
    setActiveDeadlineTab(tab) {
      this.activeDeadlineTab = tab;
      this.fetchDeadlineItems();
    },
    setActiveDeadlineStatusTab(statusTab) {
      this.activeDeadlineStatusTab = statusTab;
      this.fetchDeadlineItems();
    },
    getActiveTodoType() {
      return this.activeTodoTab;
    },
    getActiveTodoStatusId() {
      const statusMap = {
        on_hold: 1,
        on_progress: 2,
        done: 3,
        ongoing: 5,
      };
      return statusMap[this.activeTodoStatusTab] || 2;
    },
    getActiveFollowUpStatusId() {
      const statusMap = {
        on_hold: 1,
        on_progress: 2,
        done: 3,
        archived: 4,
        ongoing: 5,
      };
      return statusMap[this.activeFollowUpStatusTab] || 2;
    },
    getActiveDeadlineStatusId() {
      const statusMap = {
        on_hold: 1,
        on_progress: 2,
      };
      return statusMap[this.activeDeadlineStatusTab] || 2;
    },
    async fetchOverview(type) {
      const overviewResponse = await apiClient.get(`/dashboard/get-${type}_overview`);
      this.renderChart(type, overviewResponse.data);
    },
    renderChart(type, overviewResponse) {
      const options = {
        series: overviewResponse.data[`${type}_overview`].data,
        chart: {
          type: 'bar',
          height: 300,
          stacked: true,
          fontFamily: 'Inter, system-ui, sans-serif',
          foreColor: '#9a9cb8',
          toolbar: { show: false },
          zoom: { enabled: true },
        },
        colors: ['#6d5dfc', '#12b886', '#f59f00', '#4f7cff'],
        plotOptions: { bar: { borderRadius: 6, columnWidth: '42%' } },
        grid: { borderColor: 'rgba(127, 130, 170, .18)', strokeDashArray: 4 },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: { position: 'bottom', offsetX: -10, offsetY: 0 },
            },
          },
        ],
        xaxis: {
          categories: overviewResponse.data[`${type}_overview`].months,
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
        },
        dataLabels: {
          enabled: false,
        },
        fill: {
          opacity: 1,
        },
      };

      if (!this.overviewChart) {
        if (document.getElementById(`hiringsources`)) {
          this.overviewChart = new ApexCharts(document.querySelector('#hiringsources'), options);
          this.overviewChart.render();
        }
      } else {
        this.overviewChart.updateSeries(overviewResponse.data[`${type}_overview`].data);
      }
    },
    renderPercentageChart(id, percentage) {
      if (!document.getElementById(`${id}_graph`)) {
        return;
      }
      const options = {
        chart: {
          height: 250,
          type: 'radialBar',
          toolbar: { show: false },
        },
        colors: ['var(--chart-color2)'],
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 225,
            hollow: {
              margin: 0,
              size: '70%',
              background: '#fff',
              dropShadow: {
                enabled: true,
                top: 3,
                left: 0,
                blur: 4,
                opacity: 0.24,
              },
            },
            track: {
              background: '#fff',
              strokeWidth: '67%',
              margin: 0,
              dropShadow: {
                enabled: true,
                top: -3,
                left: 0,
                blur: 4,
                opacity: 0.35,
              },
            },
            dataLabels: {
              showOn: 'always',
              name: {
                offsetY: -10,
                show: true,
                color: '#888',
                fontSize: '17px',
              },
              value: {
                formatter: (val) => parseInt(val),
                color: '#111',
                fontSize: '36px',
                show: true,
              },
            },
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: ['var(--chart-color2)'],
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        },
        series: [percentage],
        stroke: { lineCap: 'round' },
        labels: ['Percent'],
      };

      if (document.getElementById(`${id}_graph`)) {
        const chart = new ApexCharts(document.getElementById(`${id}_graph`), options);
        chart.render();
      }
    },
  },
  setup() {
    const userInfo = ref(null);
    const dashboardItems = ref([]);
    const allDashboardItems = ref([]);
    const toast = useToast();


    const saveDashboardPreferences = async () => {
      // Extract selected item IDs
      const selectedItemIds = allDashboardItems.value
        .filter(item => item.isActive)
        .map(item => item.id);

      const response = await save(selectedItemIds); // Pass as argument

      if (response.data.success) {
        toast.success(response.data.message);
        $("#dashboard-confirmation-modal").modal("hide");
        fetchDashItems(); // Refresh dashboard items
      } else {
        toast.error(response.data.message || 'Save failed');
      }
    };



    // Only the user's name is needed here (greeting); the avatar and client switcher live in MainLayout.
    const fetchUserData = async () => {
      try {
        const response = await fetchUserInfo();
        userInfo.value = response.data?.info || null;
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    const fetchDashItems = async () => {
      try {
        const response = await fetchDashboardItems();

        // Convert numeric isActive to boolean explicitly
        allDashboardItems.value = response.data.data.map(item => ({
          ...item,
          isActive: item.isActive === 1
        }));

        dashboardItems.value = allDashboardItems.value.filter(item => item.isActive === true);

        console.log('All items: ', allDashboardItems.value);
      } catch (error) {
        console.error('Error fetching dashboard items:', error);
      }
    };



    onMounted(async () => {
      fetchDashItems();

      if (!userInfo.value) {
        fetchUserData(); // Fetch user info if not already cached
      }


    });
    return { userInfo, dashboardItems, allDashboardItems, saveDashboardPreferences };
  },

  async mounted() {
    let response = await this.fetchStatistics();

    const data = response.data.data || {};
    data.top_worked_projects_last_month_show = data.top_worked_projects_last_month && data.top_worked_projects_last_month.length ? true : false;

    this.hostingStatus = data.hosting_status || {};
    this.upcomingHoliday = data.upcoming_holiday || {};
    this.daysOff = data.days_off || {};
    this.participation = data.participation || {};
    this.employeesStatus = data.employees_status || {};
    this.topWorkedProjects = data.top_worked_projects_last_month || [];

    this.widgets = [
      {
        title: 'Projects completed',
        code: 'percent_projects_completed',
        graphId: 'percent_projects_completed_graph',
        percentage: data.percent_projects_completed,
        enabled: true,
      },
      {
        title: 'Tasks completed',
        code: 'percent_tasks_completed',
        graphId: 'percent_tasks_completed_graph',
        percentage: data.percent_tasks_completed,
        enabled: true,
      },
      {
        title: 'Subtasks completed',
        code: 'percent_subtasks_completed',
        graphId: 'percent_subtasks_completed_graph',
        percentage: data.percent_subtasks_completed,
        enabled: true,
      },
    ];

    this.$nextTick(() => {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map((tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl));

      this.fetchOverview('project');

      if (data.percent_projects_completed) {
        this.renderPercentageChart('percent_projects_completed', data.percent_projects_completed);
      }
      if (data.percent_tasks_completed) {
        this.renderPercentageChart('percent_tasks_completed', data.percent_tasks_completed);
      }
      if (data.percent_subtasks_completed) {
        this.renderPercentageChart('percent_subtasks_completed', data.percent_subtasks_completed);
      }
    });

    this.fetchTodoItems();
    this.fetchFollowUpItems();
    this.fetchDeadlineItems();
    response = await this.fetchUserWidgets();
    if (response.data.success && response.data && response.data.data.length) {
      response.data.data.forEach(widget => {
        switch (widget.widget_code) {
          case 'overview':
            this.overviewEnabled = widget.active;
            break;
          case 'hosting_status':
            this.hostingStatusEnabled = widget.active;
            break;
          case 'todo':
            this.todoEnabled = widget.active;
            break;
          case 'followUp':
            this.followUpEnabled = widget.active;
            break;
          case 'deadline_coming_soon':
            this.deadlineEnabled = widget.active;
            break;
          case 'upcoming_holiday':
            this.upcomingHolidayEnabled = widget.active;
            break;
          case 'days_off':
            this.daysOffEnabled = widget.active;
            break;
          case 'participation':
            this.participationEnabled = widget.active;
            break;
          case 'employees_status':
            this.employeesStatusEnabled = widget.active;
            break;
          case 'top_worked_projects_last_month':
            this.topWorkedProjectsEnabled = widget.active;
            break;
          default:
            const foundWidget = this.widgets.find(w => w.code === widget.widget_code);
            if (foundWidget) {
              foundWidget.enabled = widget.active;
            }
            break;
        }
      });
    }
  },
};

</script>

<style scoped>
.wx-dash { display: flex; flex-direction: column; gap: 16px; }
.wx-dhero { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.wx-dhero p { margin: 0 0 2px; font-size: 13px; color: var(--wx-text-3); font-weight: 500; }
.wx-dhero h2 { margin: 0; font-size: 30px; font-weight: 800; letter-spacing: -.035em; }
.wx-dhero-actions { display: flex; gap: 8px; }

.wx-quick { display: flex; gap: 8px; flex-wrap: wrap; }
.wx-q {
  display: inline-flex; align-items: center; gap: 10px; padding: 7px 14px 7px 7px; border-radius: 16px;
  background: var(--wx-card); box-shadow: var(--wx-sh); color: var(--wx-text) !important; font-weight: 600; font-size: 13px;
  text-decoration: none; transition: transform .15s, box-shadow .15s;
}
.wx-q:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(60, 50, 160, .14); }
.wx-qi { width: 32px; height: 32px; border-radius: 11px; display: grid; place-items: center; background: var(--wx-accent-soft); color: var(--wx-accent); }

.wx-bento { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 16px; align-items: start; }
.wx-bento > .card { margin: 0; }
.wx-s4 { grid-column: span 4; }
.wx-s6 { grid-column: span 6; }
.wx-s8 { grid-column: span 8; }
.wx-stack { grid-row: span 2; display: flex; flex-direction: column; gap: 16px; }
.wx-stack > .card { margin: 0; }

.wx-kpi {
  grid-column: span 3; border-radius: 22px; padding: 18px 20px; color: #fff !important; position: relative; overflow: hidden;
  min-height: 132px; display: block; text-decoration: none;
}
.wx-kpi::after { content: ""; position: absolute; right: -30px; bottom: -50px; width: 140px; height: 140px; border-radius: 50%; background: rgba(255, 255, 255, .14); }
.wx-kpi .l { font-size: 12.5px; font-weight: 600; opacity: .92; }
.wx-kpi .v { font-size: 34px; font-weight: 800; letter-spacing: -.03em; margin-top: 8px; line-height: 1.1; }
.wx-kpi .v small { font-size: 15px; font-weight: 600; opacity: .8; }
.wx-kpi-row { display: flex; gap: 14px; font-size: 12.5px; margin-top: 6px; opacity: .92; position: relative; z-index: 1; }
.wx-kpi.k1 { background: linear-gradient(135deg, #6d5dfc, #9b6dff); }
.wx-kpi.k2 { background: linear-gradient(135deg, #12b886, #38d9a9); }
.wx-kpi.k3 { background: linear-gradient(135deg, #4f7cff, #7aa7ff); }
.wx-kpi.k4 { background: linear-gradient(135deg, #f59f00, #ffc94a); color: #3b2a00 !important; }
.wx-ring {
  position: absolute; right: 18px; top: 18px; width: 52px; height: 52px; border-radius: 50%;
  background: conic-gradient(#fff 0 var(--p), rgba(255, 255, 255, .25) 0); display: grid; place-items: center; z-index: 1;
}
.wx-ring span { width: 40px; height: 40px; border-radius: 50%; display: grid; place-items: center; font-size: 10.5px; font-weight: 700; background: rgba(0, 0, 0, .12); backdrop-filter: blur(4px); }

.wx-holiday { display: flex; align-items: center; gap: 14px; }
.wx-hi { width: 46px; height: 46px; border-radius: 15px; background: var(--wx-card); display: grid; place-items: center; font-size: 22px; flex: none; }
.wx-holiday b { font-size: 16px; }
.wx-muted { color: var(--wx-text-3); font-size: 12.5px; }
.wx-empty { padding: 20px 0; text-align: center; }

.wx-team { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.wx-team.three { grid-template-columns: repeat(3, 1fr); }
.wx-team div { padding: 14px 10px; border-radius: 16px; text-align: center; background: color-mix(in srgb, var(--c) 12%, transparent); }
.wx-team b { display: block; font-size: 24px; font-weight: 800; color: var(--c); letter-spacing: -.02em; }
.wx-team span { font-size: 11.5px; color: var(--wx-text-2); font-weight: 500; }

.wx-bar-row { margin-bottom: 14px; }
.wx-bar-top { display: flex; justify-content: space-between; gap: 8px; font-size: 13px; margin-bottom: 6px; }
.wx-bar-top span { color: var(--wx-text-2); font-weight: 600; white-space: nowrap; }
.wx-prog { height: 8px; border-radius: 99px; background: var(--wx-soft); overflow: hidden; }
.wx-prog i { display: block; height: 100%; border-radius: 99px; background: var(--wx-g-proj); }

.wx-li {
  display: flex; align-items: center; gap: 12px; padding: 11px 12px; border-radius: 14px; color: var(--wx-text) !important;
  text-decoration: none; transition: background .12s;
}
.wx-li + .wx-li { margin-top: 2px; }
.wx-li:hover { background: var(--wx-soft); }
.wx-li-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.wx-li-main b { font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wx-dot { width: 10px; height: 10px; border-radius: 50%; padding: 0 !important; background: var(--c) !important; flex: none; font-size: 0; }
.wx-date { font-size: 12px; font-weight: 600; color: var(--wx-text-2); background: var(--wx-soft); padding: 4px 9px; border-radius: 8px; white-space: nowrap; }
.wx-date.late { color: var(--wx-late); background: color-mix(in srgb, var(--wx-late) 10%, transparent); }

.wx-check-row { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 12px; cursor: pointer; color: var(--wx-text); font-size: 14px; font-weight: 500; }
.wx-check-row:hover { background: var(--wx-soft); }
.wx-check-row .form-check-input { margin: 0; }

@media (max-width: 1200px) {
  .wx-kpi { grid-column: span 6; }
  .wx-s4, .wx-s6, .wx-s8 { grid-column: span 12; }
}
@media (max-width: 640px) {
  .wx-kpi { grid-column: span 12; }
  .wx-dhero h2 { font-size: 24px; }
  .wx-li .wx-date { display: none; }
}
</style>
